import React from 'react'
import { theme } from '../../styles/theme'

export type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'ai' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  loading?: boolean
  icon?: React.ReactNode
  fullWidth?: boolean
}

export const Button: React.FC<ButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  icon,
  fullWidth = false,
  disabled,
  ...props
}) => {
  const baseStyles: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: 'none',
    borderRadius: '8px',
    fontFamily: theme.typography.fontFamily,
    fontWeight: 500,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    opacity: disabled || loading ? 0.6 : 1,
    width: fullWidth ? '100%' : 'auto',
  }

  const sizeStyles: Record<ButtonSize, React.CSSProperties> = {
    sm: {
      height: '32px',
      padding: '0 12px',
      fontSize: '13px',
    },
    md: {
      height: '40px',
      padding: '0 16px',
      fontSize: '14px',
    },
    lg: {
      height: '48px',
      padding: '0 24px',
      fontSize: '16px',
    },
  }

  const variantStyles: Record<ButtonVariant, React.CSSProperties> = {
    primary: {
      backgroundColor: theme.colors.primary,
      color: '#FFFFFF',
    },
    secondary: {
      backgroundColor: '#FFFFFF',
      color: theme.colors.primary,
      border: `1px solid ${theme.colors.primary}`,
    },
    outline: {
      backgroundColor: 'transparent',
      color: theme.colors.textPrimary,
      border: '1px solid #E2E8F0',
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.textSecondary,
    },
    ai: {
      background: theme.ai.gradient,
      color: '#FFFFFF',
      boxShadow: theme.ai.glow,
    },
    danger: {
      backgroundColor: '#E53E3E',
      color: '#FFFFFF',
    },
  }

  const hoverStyles: Record<ButtonVariant, React.CSSProperties> = {
    primary: { backgroundColor: '#3A7BC8' },
    secondary: { backgroundColor: '#F0F7FF' },
    outline: { backgroundColor: '#F5F7FA', borderColor: '#CBD5E0' },
    ghost: { backgroundColor: '#F5F7FA' },
    ai: { transform: 'scale(1.02)' },
    danger: { backgroundColor: '#C53030' },
  }

  const [isHover, setIsHover] = React.useState(false)

  return (
    <button
      {...props}
      disabled={disabled || loading}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      style={{
        ...baseStyles,
        ...sizeStyles[size],
        ...variantStyles[variant],
        ...(isHover && !disabled && !loading ? hoverStyles[variant] : {}),
      }}
    >
      {loading && (
        <span style={{
          width: '16px',
          height: '16px',
          border: '2px solid currentColor',
          borderTopColor: 'transparent',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite',
        }} />
      )}
      {!loading && icon}
      {children}
    </button>
  )
}
